import { makeAutoObservable, runInAction } from 'mobx'
import { PurchaseService } from '~/shared/api/services/purchase'
import { PurchaseItemWithId } from '~/shared/api/services/purchase/types'
import { PurchaseFilters, PurchaseLoadingType } from './types'
import { handleAxiosError } from '~/shared/lib'
import { GlobalService } from '~/shared/api/services/global'
import { AvailabilitesProduct } from '~/shared/api/services/global/types'
import { generateUEID } from '~/shared/lib/helper-functions/ueid'

const INITIAL_FILTERS: PurchaseFilters = {
  storeId: null,
  manufacturerId: null,
  orderQuery: '',
  productIds: [],
}

export class PurchaseStore {
  purchases: PurchaseItemWithId[] = []

  aviabilities: AvailabilitesProduct[] = []

  selectAll: string[] = []

  isSelectAll: boolean = false

  deletingPurchaseId: string | null = null

  loaders: PurchaseLoadingType = {
    purchases: false,
    loadMorePurchases: false,
    updatePurchaseState: false,
    deletePurchase: false,
    aviabilities: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  filters: PurchaseFilters = INITIAL_FILTERS

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof PurchaseLoadingType) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof PurchaseLoadingType) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setSelectAll = (values: string[], isAll: boolean = this.isSelectAll) => {
    this.selectAll = values
    this.isSelectAll = isAll
  }

  toggleSelect = (id: string) => {
    if (this.selectAll.includes(id)) {
      this.selectAll = this.selectAll.filter((selectedId) => selectedId !== id)
      this.isSelectAll = false
      return
    }

    this.selectAll = [...this.selectAll, id]
    this.isSelectAll = this.selectAll.length === this.purchases.length
  }

  updateSelectedState = async (targetState: 'stock' | 'purchased') => {
    const selectedItems = this.purchases.filter((purchase) =>
      this.selectAll.includes(purchase.id),
    )

    if (!selectedItems.length) return

    this.activeLoader('updatePurchaseState')

    try {
      await PurchaseService.updatePurchaseState({
        demands: selectedItems.map((item) => {
          const { id, ...rest } = item
          return {
            ...rest,
            manufacturerName: item.manufacturerName || null,
            variationId: item.variationId || null,
            orderId: item.orderId || null,
            vendorCode: item.vendorCode || null,
            systemName: item.systemName || null,
            color: item.color || null,
            size: item.size || null,
            height: item.height || null,
            state: 'selected',
            oldState: item.state || null,
            otherOptions: item.otherOptions.map((option) => ({
              name: option.name || null,
              value: option.value || null,
            })),
            orderNumber: item.orderNumber === '' ? null : item.orderNumber,
            optionsText: item.optionsText || null,
          }
        }),
        state: targetState,
      })

      runInAction(() => {
        this.selectAll = []
        this.isSelectAll = false
      })

      await this.getPurchases()
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('updatePurchaseState')
    }
  }

  setFilters = (filters: PurchaseFilters) => {
    this.filters = filters
  }

  clearAviabilities = () => {
    this.aviabilities = []
  }

  getPurchases = async (options?: {
    action?: 'loadMorePurchases'
    filters?: PurchaseFilters
  }) => {
    const { action } = options || {}
    const filters = options?.filters || this.filters

    if (action === 'loadMorePurchases' && this.requestSettings.isAllLoaded) {
      return
    }

    this.activeLoader(action || 'purchases')

    try {
      if (action !== 'loadMorePurchases') {
        runInAction(() => {
          this.filters = filters
        })
      }

      const params = new URLSearchParams()

      params.set('limit', String(this.requestSettings.limit))
      params.set(
        'offset',
        String(
          action === 'loadMorePurchases' ? this.requestSettings.offset : 0,
        ),
      )

      if (filters.manufacturerId) {
        params.set('manufacturerId', filters.manufacturerId)
      }

      if (filters.orderQuery.trim()) {
        const orderQuery = filters.orderQuery.trim()
        params.set('orderNumber', orderQuery)
      }

      if (filters.productIds.length > 0) {
        filters.productIds.forEach((productId) => {
          params.append('products[]', productId)
        })
      }

      const { data } = await PurchaseService.getPurchases({ params })

      const dataWithId = data.map((item) => ({
        ...item,
        id: generateUEID(),
      }))

      if (action === 'loadMorePurchases') {
        runInAction(() => {
          this.purchases = [...this.purchases, ...dataWithId]
          if (this.isSelectAll) {
            this.selectAll = [
              ...this.selectAll,
              ...dataWithId.map((item) => item.id),
            ]
          }
        })
      } else {
        runInAction(() => {
          this.purchases = dataWithId
          if (this.isSelectAll) {
            this.selectAll = dataWithId.map((item) => item.id)
          }
        })
      }

      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMorePurchases'
              ? this.requestSettings.offset + this.requestSettings.limit
              : this.requestSettings.limit,
        }
      })
      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'purchases')
    }
  }

  deletePurchase = async (demandId: string) => {
    this.activeLoader('deletePurchase')

    try {
      await PurchaseService.deletePurchaseDemand(demandId)

      runInAction(() => {
        this.selectAll = []
        this.isSelectAll = false
      })

      await this.getPurchases()
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.deletingPurchaseId = null
      })
      this.closeLoader('deletePurchase')
    }
  }

  getAviabilities = async (
    storeId?: string,
    manufacturerId?: string,
    q?: string,
    productIds: string[] = [],
  ) => {
    try {
      this.activeLoader('aviabilities')
      if (!storeId) {
        runInAction(() => {
          this.aviabilities = []
        })
        return
      }

      const params = new URLSearchParams()

      params.set('demands', 'true')
      params.set('storeId', storeId)

      if (manufacturerId) {
        params.set('manufacturerId', manufacturerId)
      }

      if (q?.trim()) {
        params.set('q', q.trim())
      }

      if (productIds.length > 0) {
        productIds.forEach((productId) => {
          params.append('products[]', productId)
        })
      }
      const { data } = await GlobalService.getAvailableProducts({ params })

      runInAction(() => {
        this.aviabilities = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('aviabilities')
    }
  }
}
